import { useEffect } from 'react';

/**
 * Hook para gerenciar o redimensionamento do viewport no iOS (iPhone/iPad) e Android.
 * Resolve o problema do teclado virtual cobrindo a barra de input e os elementos de rodapé.
 */
export function useViewportFix() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;

    const isIOSDevice = () => {
      return (
        (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
        !window.MSStream
      );
    };

    let fallbackTimeout = null;

    const updateViewport = () => {
      if (window.visualViewport) {
        const vv = window.visualViewport;
        const height = Math.round(vv.height);
        const top = Math.round(vv.offsetTop);
        const width = Math.round(vv.width);

        // Estima a altura do teclado comparando com innerHeight
        const windowH = window.innerHeight;
        const keyboardHeight = Math.max(0, windowH - height - top);

        root.style.setProperty('--visual-viewport-height', `${height}px`);
        root.style.setProperty('--visual-viewport-top', `${top}px`);
        root.style.setProperty('--visual-viewport-width', `${width}px`);
        root.style.setProperty('--keyboard-height', `${keyboardHeight}px`);

        if (keyboardHeight > 100) {
          document.body.classList.add('keyboard-open');
          // Se o visualViewport já detectou redução de altura, limpa qualquer fallback
          root.style.setProperty('--keyboard-fallback-offset', '0px');
        } else {
          document.body.classList.remove('keyboard-open');
        }
      } else {
        root.style.setProperty('--visual-viewport-height', `${window.innerHeight}px`);
        root.style.setProperty('--visual-viewport-top', '0px');
        root.style.setProperty('--visual-viewport-width', '100%');
        root.style.setProperty('--keyboard-height', '0px');
        root.style.setProperty('--keyboard-fallback-offset', '0px');
      }
    };

    // Atualização inicial
    updateViewport();

    // Eventos do Visual Viewport
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewport, { passive: true });
      window.visualViewport.addEventListener('scroll', updateViewport, { passive: true });
    }

    window.addEventListener('resize', updateViewport, { passive: true });
    
    const handleOrientationChange = () => {
      setTimeout(updateViewport, 100);
      setTimeout(updateViewport, 300);
    };
    window.addEventListener('orientationchange', handleOrientationChange, { passive: true });

    // Previne rolagem desnecessária do body no iOS quando o teclado está aberto,
    // mantendo o cabeçalho fixo na tela
    const handleWindowScroll = () => {
      if (document.body.classList.contains('keyboard-open') || isIOSDevice()) {
        if (window.scrollY !== 0 || window.scrollX !== 0) {
          window.scrollTo(0, 0);
        }
      }
    };
    window.addEventListener('scroll', handleWindowScroll, { passive: true });

    // Trata foco/blur global em campos de texto (input/textarea)
    const handleFocusIn = (e) => {
      const target = e.target;
      if (!target) return;
      const tagName = target.tagName ? target.tagName.toLowerCase() : '';
      if (tagName === 'textarea' || tagName === 'input') {
        // Imediatamente tenta manter o window em (0, 0)
        window.scrollTo(0, 0);

        if (isIOSDevice()) {
          if (fallbackTimeout) clearTimeout(fallbackTimeout);
          // Caso específico de iframe isolado no iOS onde visualViewport pode não disparar:
          // verifica após 350ms se o viewport reduziu; se não reduziu, ativa o fallback de teclado
          fallbackTimeout = setTimeout(() => {
            const vv = window.visualViewport;
            const isReduced = vv && (vv.height < window.innerHeight - 80);
            if (!isReduced && (window.innerWidth <= 768)) {
              // Teclado padrão de iPhone em modo retrato (~336px com barra de acessórios Done)
              root.style.setProperty('--keyboard-fallback-offset', '336px');
              document.body.classList.add('keyboard-open');
            }
          }, 350);
        }
      }
    };

    const handleFocusOut = (e) => {
      const target = e.target;
      if (!target) return;
      const tagName = target.tagName ? target.tagName.toLowerCase() : '';
      if (tagName === 'textarea' || tagName === 'input') {
        if (fallbackTimeout) clearTimeout(fallbackTimeout);
        root.style.setProperty('--keyboard-fallback-offset', '0px');
        setTimeout(() => {
          updateViewport();
          window.scrollTo(0, 0);
        }, 100);
      }
    };

    document.addEventListener('focusin', handleFocusIn, { passive: true });
    document.addEventListener('focusout', handleFocusOut, { passive: true });

    // Suporte a mensagens do host nativo via postMessage (se o app nativo informar a altura do teclado)
    const handleMessage = (event) => {
      const data = event.data;
      if (!data) return;
      if (data.type === 'keyboard_did_show' && data.keyboardHeight) {
        const kh = Number(data.keyboardHeight);
        root.style.setProperty('--keyboard-height', `${kh}px`);
        root.style.setProperty('--visual-viewport-height', `${window.innerHeight - kh}px`);
        document.body.classList.add('keyboard-open');
      } else if (data.type === 'keyboard_did_hide') {
        document.body.classList.remove('keyboard-open');
        root.style.setProperty('--keyboard-fallback-offset', '0px');
        updateViewport();
      }
    };
    window.addEventListener('message', handleMessage);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewport);
        window.visualViewport.removeEventListener('scroll', updateViewport);
      }
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('scroll', handleWindowScroll);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      window.removeEventListener('message', handleMessage);
      if (fallbackTimeout) clearTimeout(fallbackTimeout);
    };
  }, []);
}

export default useViewportFix;
